import * as React from 'react';
import styled, { css } from 'styled-components';
import { color, ColorProps, space, SpaceProps } from '../../style/mixins';

export interface IIcon extends ColorProps, SpaceProps {
  size?: string;
  block?: boolean;
  rotation?: string;
}

export const Icon = styled.svg.attrs<IIcon>((props) => ({
  xmlns: 'http://www.w3.org/2000/svg',
  size: props.size || '1em',
}))`
  ${color}
  ${space}

  display: inline-block;
  vertical-align: middle;
  width: ${({ size }: IIcon) => size};
  height: ${({ size }: IIcon) => size};
  fill: ${({ fill }) => fill || 'currentColor'};
  transition: transform 150ms ease-in-out;

  ${({ rotation }) =>
    rotation && {
      transform: `rotate(${rotation})`,
    }}

  ${({ block }: IIcon) =>
    block &&
    css`
      display: block;
    `}
`;

export const IconChevronDown = (props: IIcon) => (
  <Icon viewBox="0 0 16 9" {...props}>
    <path d="M1.00008 0.292969L8.00008 7.29297L15.0001 0.292969L15.7072 1.00008L8.00008 8.70718L0.292969 1.00008L1.00008 0.292969Z" />
  </Icon>
);

export const IconChevronDownSmall = (props: IIcon) => (
  <Icon viewBox="0 0 12 12" {...props}>
    <path d="M6.00015 8.33119L1.83233 4.62646L1.16797 5.37387L6.00015 9.66915L10.8323 5.37387L10.168 4.62646L6.00015 8.33119Z" />
  </Icon>
);

export const IconChevronLeft = (props: IIcon) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path d="M8.3 12.7l6 6c.2.2.4.3.7.3s.5-.1.7-.3c.4-.4.4-1 0-1.4L10.4 12l5.3-5.3c.4-.4.4-1 0-1.4s-1-.4-1.4 0l-6 6c-.4.4-.4 1 0 1.4z" />
  </Icon>
);

export const IconChevronRight = (props: IIcon) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path d="M15.7 12.7l-6 6c-.2.2-.4.3-.7.3s-.5-.1-.7-.3c-.4-.4-.4-1 0-1.4l5.3-5.3-5.3-5.3c-.4-.4-.4-1 0-1.4s1-.4 1.4 0l6 6c.4.4.4 1 0 1.4z" />
  </Icon>
);

export const IconInfoRound = (props: IIcon) => (
  <Icon viewBox="0 0 12 12" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11ZM6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12ZM5.5 7.9V2.5H6.5V7.9H5.5ZM5.5 8.5V9.7H6.5V8.5H5.5Z"
    />
  </Icon>
);

export const IconClose = (props: IIcon) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path stroke="currentColor" d="M4.222 4.222l15.556 15.556M4.222 19.778L19.778 4.222"></path>
  </Icon>
);

export const IconPlus = (props: IIcon) => (
  <Icon viewBox="0 0 12 12" {...props}>
    <path d="M5.5 6.5V12H6.5V6.5H12V5.5H6.5V0H5.5V5.5H0V6.5H5.5Z" />
  </Icon>
);

export const IconCloseSmall = (props: IIcon) => (
  <Icon viewBox="0 0 16 16" {...props}>
    <path d="M8.00008 8.7076L5.52547 11.1822L4.81836 10.4751L7.29297 8.00049L4.81775 5.52528L5.52486 4.81817L8.00008 7.29338L10.4752 4.81824L11.1823 5.52535L8.70718 8.00049L11.1817 10.475L10.4746 11.1821L8.00008 8.7076Z" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z"
    />
  </Icon>
);

export const IconMenu = (props: IIcon) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <rect x="1" y="4" width="22" height="1" rx=".5"></rect>
    <rect x="5" y="11" width="18" height="1" rx=".5"></rect>
    <rect x="3" y="18" width="20" height="1" rx=".5"></rect>
  </Icon>
);

export const IconMeatballs = (props: IIcon) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path d="M5 12C5 12.8284 4.32843 13.5 3.5 13.5C2.67157 13.5 2 12.8284 2 12C2 11.1716 2.67157 10.5 3.5 10.5C4.32843 10.5 5 11.1716 5 12ZM13.5 12C13.5 12.8284 12.8284 13.5 12 13.5C11.1716 13.5 10.5 12.8284 10.5 12C10.5 11.1716 11.1716 10.5 12 10.5C12.8284 10.5 13.5 11.1716 13.5 12ZM20.5 13.5C21.3284 13.5 22 12.8284 22 12C22 11.1716 21.3284 10.5 20.5 10.5C19.6716 10.5 19 11.1716 19 12C19 12.8284 19.6716 13.5 20.5 13.5Z" />
  </Icon>
);

export const IconSearch = (props: IIcon) => (
  <Icon viewBox="0 0 19 19" {...props}>
    <path d="M18.9 18.1l-4-4c1.3-1.5 2.2-3.5 2.2-5.6C17 3.8 13.2 0 8.5 0 3.8 0 0 3.8 0 8.5S3.8 17 8.5 17c2.2 0 4.1-.8 5.6-2.2l4 4c.1.1.2.1.4.1s.3 0 .4-.1c.1-.1.1-.5 0-.7zM1 8.5C1 4.4 4.4 1 8.5 1S16 4.4 16 8.5c0 2.1-.8 3.9-2.2 5.3-1.4 1.3-3.2 2.2-5.3 2.2C4.4 16 1 12.6 1 8.5z" />
  </Icon>
);

export const IconSearchBold = (props: IIcon) => (
  <Icon viewBox="0 0 16 16" {...props}>
    <path d="M14.7 13l-3-3c.6-.9 1-2 1-3.2C12.7 3.6 10 1 6.8 1S1 3.6 1 6.8s2.6 5.8 5.8 5.8c1.2 0 2.3-.4 3.2-1l3 3c.2.2.5.3.8.3.3 0 .6-.1.8-.3.5-.4.5-1.1.1-1.6zM3.3 6.8c0-1.9 1.6-3.5 3.5-3.5s3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5-3.5-1.5-3.5-3.5z" />
  </Icon>
);

export const IconCheck = (props: IIcon) => (
  <Icon viewBox="0 0 16 16" {...props}>
    <path d="M16 3.72957L6.00088 14L0 7.83618L0.710306 7.10661L6.00088 12.5409L15.2897 3L16 3.72957Z" />
  </Icon>
);

export const IconGraph = (props: IIcon) => (
  <Icon viewBox="0 0 21 21" {...props}>
    <path d="M0.599976 19.5V0H1.59998V19H20.6V20H1.09998H0.599976V19.5ZM17.6 12.2718V8H18.6V13.5V14H18.1H12.6V13H16.9122L9.08973 4.71735L4.95353 8.85355L4.24642 8.14645L8.74642 3.64645L9.11022 3.28265L9.46348 3.65669L17.6 12.2718Z" />
  </Icon>
);

export const IconGauge = (props: IIcon) => (
  <Icon viewBox="0 -1 22 22" {...props}>
    <path d="M4.88795 1.86182C6.69695 0.648001 8.82397 0 11 0C13.1761 0 15.3031 0.648002 17.1121 1.86182C18.9211 3.07561 20.3307 4.80061 21.163 6.81842C21.9953 8.83622 22.213 11.0564 21.7887 13.1984C21.3645 15.3403 20.3172 17.3081 18.7789 18.8528L18.0704 18.1472C19.469 16.7426 20.4218 14.9528 20.8078 13.0041C21.1938 11.0553 20.9957 9.03533 20.2385 7.19974C19.4814 5.36417 18.1994 3.79562 16.5549 2.69221C14.9105 1.58881 12.9774 1 11 1C9.02269 1 7.08957 1.58881 5.44514 2.69221C3.80068 3.79562 2.51866 5.36417 1.76152 7.19974C1.00438 9.03533 0.806246 11.0553 1.19226 13.0041C1.57827 14.9528 2.53102 16.7426 3.92971 18.1472L3.22112 18.8528C1.6829 17.3081 0.635596 15.3403 0.211319 13.1984C-0.212955 11.0564 0.00477916 8.83622 0.837077 6.81842C1.66938 4.80061 3.07899 3.07561 4.88795 1.86182ZM13.8 11C13.8 12.5464 12.5464 13.8 11 13.8C9.45365 13.8 8.20004 12.5464 8.20004 11C8.20004 10.4074 8.38413 9.85784 8.69823 9.40531L4.29294 5.00001L5.00004 4.29291L9.40534 8.6982C9.85787 8.3841 10.4075 8.20001 11 8.20001C12.5464 8.20001 13.8 9.45362 13.8 11ZM11 12.8C11.9942 12.8 12.8 11.9941 12.8 11C12.8 10.0059 11.9942 9.20001 11 9.20001C10.0059 9.20001 9.20004 10.0059 9.20004 11C9.20004 11.9941 10.0059 12.8 11 12.8Z" />
  </Icon>
);

export const IconGearbox = (props: IIcon) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path d="M5 6.91465C5.5826 6.70873 6 6.15311 6 5.5C6 4.67157 5.32843 4 4.5 4C3.67157 4 3 4.67157 3 5.5C3 6.15311 3.4174 6.70873 4 6.91465V10.5C4 11.8807 5.11929 13 6.5 13H11V18.0854C10.4174 18.2913 10 18.8469 10 19.5C10 20.3284 10.6716 21 11.5 21C12.3284 21 13 20.3284 13 19.5C13 18.8469 12.5826 18.2913 12 18.0854V13H18V18.0854C17.4174 18.2913 17 18.8469 17 19.5C17 20.3284 17.6716 21 18.5 21C19.3284 21 20 20.3284 20 19.5C20 18.8469 19.5826 18.2913 19 18.0854V6.91465C19.5826 6.70873 20 6.15311 20 5.5C20 4.67157 19.3284 4 18.5 4C17.6716 4 17 4.67157 17 5.5C17 6.15311 17.4174 6.70873 18 6.91465V12H12V6.91465C12.5826 6.70873 13 6.15311 13 5.5C13 4.67157 12.3284 4 11.5 4C10.6716 4 10 4.67157 10 5.5C10 6.15311 10.4174 6.70873 11 6.91465V12H6.5C5.67157 12 5 11.3284 5 10.5V6.91465Z" />
  </Icon>
);

export const IconEngine = (props: IIcon) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path d="M10 4.0303H6V3L15 3.00095V4.03125L11 4.0303V6H13.9986L15.9986 7.9697H18V9.9697H20V8H22C23.105 8 24 8.92212 24 10.0606V17.9394C24 19.0779 23.105 20 22 20H20V18.0095H18V20H8.65477L6 17H3V13H1V17H0V8H1V11.9697H3V8H6V6H10V4.0303ZM13.5836 7.0303H7V9.0303H4V15.9697H6.415L9.06977 18.9909H17V16.9999H21V18.9697H22C22.55 18.9697 23 18.5061 23 17.9394V10.0606C23 9.49394 22.55 9.0303 22 9.0303H21V11H17V9H15.5836L13.5836 7.0303Z" />
  </Icon>
);

export const IconDriveType = (props: IIcon) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.170776 7.04119L2.6536 0.462891L5.57729 1.56637L3.09447 8.14467L0.170776 7.04119ZM3.23607 1.75158L4.2886 2.14884L2.512 6.85598L1.45947 6.45872L3.23607 1.75158Z"
    />
    <path d="M4.28153 5H7.08563C7.23628 5.42621 7.57407 5.764 8.00028 5.91465V18.0854C7.57407 18.236 7.23628 18.5738 7.08563 19H3.96903V16H0.96903V23H3.96903V20H7.08563C7.29155 20.5826 7.84717 21 8.50028 21C9.15339 21 9.70901 20.5826 9.91493 20H13.0003V23H16.0003V16H13.0003V19H9.91493C9.76429 18.5738 9.42649 18.236 9.00028 18.0854V5.91465C9.42649 5.764 9.76429 5.42621 9.91493 5H13.1931L12.4227 7.04119L15.3464 8.14467L17.8292 1.56637L14.9056 0.462891L13.5706 4H9.91493C9.70901 3.4174 9.15339 3 8.50028 3C7.84717 3 7.29155 3.4174 7.08563 4H4.65896L4.28153 5ZM8.50028 19C8.77642 19 9.00028 19.2239 9.00028 19.5C9.00028 19.7761 8.77642 20 8.50028 20C8.22414 20 8.00028 19.7761 8.00028 19.5C8.00028 19.2239 8.22414 19 8.50028 19ZM8.50028 4C8.77642 4 9.00028 4.22386 9.00028 4.5C9.00028 4.77614 8.77642 5 8.50028 5C8.22871 5 8.0077 4.78349 8.00046 4.51366V4.48634C8.0077 4.21651 8.22871 4 8.50028 4ZM15.0003 22H14.0003V17H15.0003V22ZM1.96903 17H2.96903V22H1.96903V17ZM15.488 1.75158L16.5406 2.14884L14.764 6.85598L13.7114 6.45872L15.488 1.75158Z" />
  </Icon>
);

export const IconCalendar = (props: IIcon) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path d="M8 2V7H9V2H8ZM15 2V7H16V2H15ZM13.5 5H10.5V4H13.5V5ZM3.5 4.00002H3V4.50002V9.50002V18C3 19.6569 4.34315 21 6 21H18C19.6569 21 21 19.6569 21 18V9.50002V4.50002V4.00002H20.5H17.5V5.00002H20V9.00002H4V5.00002H6.5V4.00002H3.5ZM4 10H20V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V10Z" />
  </Icon>
);

export const IconWarning = (props: IIcon) => (
  <Icon viewBox="0 0 38 35" {...props}>
    <path
      fillRule="evenodd"
      d="M19.0007 10L29.8578 30H8.14355L19.0007 10ZM9.82426 29L19.0007 12.096L28.1771 29H9.82426Z"
    />
    <path fillRule="evenodd" d="M19 0L38 35H0L19 0ZM1.6807 34L19 2.09603L36.3193 34H1.6807Z" />
  </Icon>
);

export const IconStar = (props: IIcon) => (
  <Icon viewBox="0 0 32 29" {...props}>
    <path d="M16 0L19.5922 11.0557H31.2169L21.8123 17.8885L25.4046 28.9443L16 22.1115L6.59544 28.9443L10.1877 17.8885L0.783095 11.0557H12.4078L16 0Z" />
  </Icon>
);

export const IconCompare = (props: IIcon) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path d="M9.33782 13.1313L4.28536 8.49992H22V7.49992H4.28536L9.33782 2.8685L8.66209 2.13135L2.26001 7.99992L8.66209 13.8685L9.33782 13.1313ZM21.7399 16.9999L15.3379 11.1313L14.6621 11.8685L19.7146 16.4999H2V17.4999H19.7146L14.6621 22.1313L15.3379 22.8685L21.7399 16.9999Z" />
  </Icon>
);

export const IconExternalLink = (props: IIcon) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path d="M6 3H12V4H6C4.89551 4 4 4.89551 4 6V18C4 19.1045 4.89551 20 6 20H18C19.1045 20 20 19.1045 20 18V12H21V18C21 19.6572 19.6567 21 18 21H6C4.34326 21 3 19.6572 3 18V6C3 4.34277 4.34326 3 6 3Z" />
    <path d="M21 3H16V4H19.293L11.6465 11.6465L12.3535 12.3535L20 4.70703V8H21V3Z" />
  </Icon>
);

export const IconInfo = (props: IIcon) => (
  <Icon viewBox="0 0 16 16" {...props}>
    <path d="M7.60872 6.36294C7.36672 6.36294 7.12472 6.41427 6.88272 6.51694L6.76172 7.01194C6.93772 6.94594 7.11372 6.91294 7.28972 6.91294C7.42172 6.91294 7.52072 6.94594 7.58672 7.01194C7.65272 7.07794 7.68572 7.1806 7.68572 7.31994C7.68572 7.38594 7.67472 7.4776 7.65272 7.59494C7.63072 7.71227 7.59405 7.85894 7.54272 8.03494C7.49872 8.21094 7.41439 8.51894 7.28972 8.95894C7.17239 9.3916 7.08439 9.71427 7.02572 9.92694C6.97439 10.1323 6.93405 10.3083 6.90472 10.4549C6.88272 10.5943 6.87172 10.7079 6.87172 10.7959C6.87172 10.9646 6.90472 11.1113 6.97072 11.2359C7.03672 11.3533 7.13939 11.4413 7.27872 11.4999C7.42539 11.5659 7.60872 11.5989 7.82872 11.5989C8.08539 11.5989 8.32739 11.5513 8.55472 11.4559L8.67572 10.9499C8.47772 11.0233 8.30172 11.0599 8.14772 11.0599C8.01572 11.0599 7.91672 11.0269 7.85072 10.9609C7.78472 10.8876 7.75172 10.7813 7.75172 10.6419C7.75172 10.5759 7.76272 10.4843 7.78472 10.3669C7.80672 10.2496 7.83972 10.1029 7.88372 9.92694C7.93505 9.7436 8.02305 9.43561 8.14772 9.00294C8.27239 8.56294 8.36039 8.24027 8.41172 8.03494C8.46305 7.8296 8.49972 7.65727 8.52172 7.51794C8.55105 7.37127 8.56572 7.2576 8.56572 7.17694C8.56572 7.00094 8.52905 6.85427 8.45572 6.73694C8.38972 6.61227 8.28339 6.5206 8.13672 6.46194C7.99739 6.39594 7.82139 6.36294 7.60872 6.36294Z" />
    <path d="M8.07072 4.43794C8.00472 4.54794 7.97172 4.66527 7.97172 4.78994C7.97172 4.96594 8.02672 5.1126 8.13672 5.22994C8.24672 5.34727 8.38972 5.40594 8.56572 5.40594C8.69039 5.40594 8.80772 5.3766 8.91772 5.31794C9.02772 5.25194 9.11205 5.16394 9.17072 5.05394C9.22939 4.94394 9.25872 4.83027 9.25872 4.71294C9.25872 4.53694 9.20372 4.39027 9.09372 4.27294C8.99105 4.14827 8.84805 4.08594 8.66472 4.08594C8.54739 4.08594 8.43372 4.11894 8.32372 4.18494C8.22105 4.2436 8.13672 4.32794 8.07072 4.43794Z" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8ZM14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z"
    />
  </Icon>
);

export const IconStarSmall = (props: IIcon) => (
  <Icon viewBox="0 0 12 12" {...props}>
    <path d="M5.71436 0L7.06144 4.1459H11.4207L7.89398 6.7082L9.24107 10.8541L5.71436 8.2918L2.18764 10.8541L3.53473 6.7082L0.00801659 4.1459H4.36727L5.71436 0Z" />
  </Icon>
);

export const IconImageFallback = (props: IIcon) => (
  <Icon viewBox="0 0 25 25" {...props}>
    <g clipPath="url(#clip0_3761_5096)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.42859 12C9.53316 12 10.4286 11.1046 10.4286 10C10.4286 8.89543 9.53316 8 8.42859 8C7.32402 8 6.42859 8.89543 6.42859 10C6.42859 11.1046 7.32402 12 8.42859 12ZM8.42859 11C8.98087 11 9.42859 10.5523 9.42859 10C9.42859 9.44772 8.98087 9 8.42859 9C7.8763 9 7.42859 9.44772 7.42859 10C7.42859 10.5523 7.8763 11 8.42859 11Z"
        fill="#141414"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.42859 6V18H20.4286V6H4.42859ZM19.4286 7H5.42859V17H6.72148L12.4286 11.2929L13.9286 12.7929L19.4286 7.29289V7ZM19.4286 8.70711L13.9286 14.2071L12.4286 12.7071L8.1357 17H19.4286V8.70711Z"
        fill="#141414"
      />
      <path
        opacity="0.2"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.428589 0H6.42859V1H1.42859V6H0.428589V0ZM18.4286 0H24.4286V6H23.4286V1H18.4286V0ZM0.428589 18H1.42859V23H6.42859V24H0.428589V18ZM23.4286 23V18H24.4286V24H18.4286V23H23.4286Z"
        fill="#141414"
      />
    </g>
    <defs>
      <clipPath id="clip0_3761_5096">
        <rect width="24" height="24" fill="white" transform="translate(0.428589)" />
      </clipPath>
    </defs>
  </Icon>
);

export const IconVerfiedBadge = (props: IIcon) => (
  <Icon fill="none" viewBox="0 0 24 24" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.06068 10.9396C1.47489 11.5254 1.47489 12.4751 2.06068 13.0609L4.2217 15.2219L4.2217 18.2783C4.2217 19.1068 4.89327 19.7783 5.7217 19.7783H8.77809L10.9391 21.9394C11.5249 22.5251 12.4746 22.5251 13.0604 21.9394L15.2215 19.7783H18.2777C19.1061 19.7783 19.7777 19.1068 19.7777 18.2783V15.2221L21.9391 13.0607C22.5249 12.4749 22.5249 11.5251 21.9391 10.9394L19.7777 8.77795V5.72199C19.7777 4.89356 19.1061 4.22199 18.2777 4.22199L15.2217 4.22199L13.0607 2.06092C12.4749 1.47513 11.5251 1.47514 10.9394 2.06092L8.77829 4.22199L5.7217 4.22199C4.89327 4.22199 4.2217 4.89356 4.2217 5.72199L4.2217 8.77858L2.06068 10.9396Z"
      fill="#2A609D"
    />
    <path d="M16 10L11 15L8 12" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
  </Icon>
);

export const IconInsecureBadge = (props: IIcon) => (
  <Icon fill="none" viewBox="0 0 24 24" {...props}>
    <circle cx="12" cy="12" r="10" fill="#EB7400" />
    <path
      d="M7 13L12 8M7 13H10M7 13V10"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 16L17 11M17 11H14M17 11V14"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Icon>
);

export const IconCopy = (props: IIcon) => (
  <Icon viewBox="0 0 16 16" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.5 1C2.11929 1 1 2.11929 1 3.5V9.5C1 10.8807 2.11929 12 3.5 12H4V13C4 14.1046 4.89543 15 6 15H13C14.1046 15 15 14.1046 15 13V6C15 4.89543 14.1046 4 13 4H12V3.5C12 2.11929 10.8807 1 9.5 1H3.5ZM11 4V3.5C11 2.67157 10.3284 2 9.5 2H3.5C2.67157 2 2 2.67157 2 3.5V9.5C2 10.3284 2.67157 11 3.5 11H4V6C4 4.89543 4.89543 4 6 4H11ZM5 6C5 5.44772 5.44772 5 6 5H13C13.5523 5 14 5.44772 14 6V13C14 13.5523 13.5523 14 13 14H6C5.44772 14 5 13.5523 5 13V6Z"
    />
  </Icon>
);

export const IconInvalidBadge = (props: IIcon) => (
  <Icon fill="none" viewBox="0 0 24 24" {...props}>
    <circle cx="12" cy="12" r="10" fill="#BF2012" />
    <path
      d="M16.8373 7.94839C17.0542 7.73143 17.0542 7.37968 16.8373 7.16272C16.6203 6.94576 16.2686 6.94576 16.0516 7.16272L12 11.2143L7.94839 7.16272C7.73143 6.94576 7.37968 6.94576 7.16272 7.16272C6.94576 7.37968 6.94576 7.73143 7.16272 7.94839L11.2143 12L7.16272 16.0516C6.94576 16.2686 6.94576 16.6203 7.16272 16.8373C7.37968 17.0542 7.73143 17.0542 7.94839 16.8373L12 12.7857L16.0516 16.8373C16.2686 17.0542 16.6203 17.0542 16.8373 16.8373C17.0542 16.6203 17.0542 16.2686 16.8373 16.0516L12.7857 12L16.8373 7.94839Z"
      fill="white"
    />
  </Icon>
);
